import React from 'react';
import { motion } from 'framer-motion';
import { X, Check, TrendingUp, Clock, ShoppingBag, Database } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const kpiIcons = [TrendingUp, Clock, ShoppingBag, Database];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut', delay },
});

export default function CommercialBenefits() {
  const { t } = useLanguage();
  const b = t.benefits;

  return (
    <section id="para-negocios" className="py-14 md:py-20 bg-[#1F2833]" data-testid="benefits-section">
      <div className="section-container">
        <motion.div className="text-center mb-14" {...fadeUp()}>
          <div className="divider-gold mx-auto" />
          <h2 className="heading-lg mb-4">
            {b.title}<br />
            <span className="text-gradient-gold">{b.titleHighlight}</span>
          </h2>
          <p className="text-[#8A9099] text-base md:text-lg max-w-2xl mx-auto">{b.body}</p>
        </motion.div>

        {/* Passive vs Active */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-16">
          <motion.div className="bg-[#171E26] rounded-2xl p-8 border border-white/5" {...fadeUp(0.1)}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-red-500/15 flex items-center justify-center">
                <X size={16} className="text-red-400" />
              </div>
              <h3 className="font-exo font-bold italic text-[#F3F1EB] text-lg">{b.passiveTitle}</h3>
            </div>
            <ul className="space-y-3">
              {b.passiveItems.map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-[#8A9099]">
                  <div className="w-5 h-5 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                    <X size={10} className="text-red-400" />
                  </div>
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div className="bg-[#0F6B49]/10 rounded-2xl p-8 border border-[#0F6B49]/40" {...fadeUp(0.15)}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-[#D5A63B]/15 flex items-center justify-center">
                <Check size={16} className="text-[#D5A63B]" />
              </div>
              <h3 className="font-exo font-bold italic text-[#F3F1EB] text-lg">{b.activeTitle}</h3>
            </div>
            <ul className="space-y-3">
              {b.activeItems.map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#0F6B49]/30 flex items-center justify-center flex-shrink-0">
                    <Check size={10} className="text-[#D5A63B]" />
                  </div>
                  <span className="text-[#F3F1EB] text-sm font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {b.kpis.map((kpi, i) => {
            const Icon = kpiIcons[i];
            return (
              <motion.div key={i} className="card-dark p-6 text-center" {...fadeUp(0.2 + i * 0.07)}
                data-testid={`kpi-card-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-[#D5A63B]/10 flex items-center justify-center mb-4 mx-auto
                  border border-[#D5A63B]/20">
                  <Icon size={22} className="text-[#D5A63B]" />
                </div>
                <h3 className="font-exo font-bold italic text-[#D5A63B] text-lg mb-2">{kpi.label}</h3>
                <p className="text-[#8A9099] text-sm leading-relaxed">{kpi.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
